/**
 * workflows/convertWorkflow.ts
 *
 * New CONVERT workflow using the extracted architecture:
 *   Source (files in folder) → BL Context → MetadataEditor → Rename only
 *
 * Replaces the legacy flow:
 *   convertCreativeFixNameErrorsScenario → initialized → fillJobs → checkSubmit → rename loop
 *
 * NOTE: BL entity attachment is still handled by the converter module
 * via `creativeAttach` in sagasConvertFolder.ts. This workflow only handles
 * the rename step within the creative uploader dialog.
 */

import type { PreparableFile, PreparedFile, CreativeBLSettings } from '../contracts';
import {
  _closeEditor,
  _setSubmitInProgress,
  requestUserValidation,
} from '../metadataEditor/MetadataEditorService';
import { renameForConvert } from '../executionAdapters/convertAdapter';
import { JobMode } from '../legacy_models/model';
import type { NodeFallback, FallbackDictionary } from '../legacy_models/fallback';
import {
  buildPreparedFiles,
  prepareMetadataDraftFiles,
  validateMetadataDraftFiles,
} from '../metadataDomain';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ConvertWorkflowInput {
  /** Files to validate/rename, with their fallback metadata */
  nodesFallbacks: NodeFallback[];
  /** BL settings already detected by the converter */
  settings: CreativeBLSettings;
  /** Skip metadata dialog for folders already marked READY when local validation passes */
  skipManualValidation?: boolean;
}

export interface ConvertWorkflowResult {
  /** Prepared files with final metadata (for the converter to use in creativeAttach) */
  preparedFiles: PreparedFile[];
  /** Diff of metadata changes applied by the user (for caching) */
  metaDiff: Record<string, string>;
}

// ─── Workflow ────────────────────────────────────────────────────────────────

/**
 * Validate and rename files during folder-to-creative conversion.
 *
 * @returns ConvertWorkflowResult, or null if user cancelled.
 */
export async function convertWorkflow(
  input: ConvertWorkflowInput,
  signal: AbortSignal,
): Promise<ConvertWorkflowResult | null> {
  const { nodesFallbacks, settings, skipManualValidation = false } = input;

  // 1. Convert to abstract PreparableFile format
  const preparableFiles: PreparableFile[] = nodesFallbacks.map(
    ({ node, relpath, issues, pathSegments, excluded, exclusionReason }) => ({
      id: `uuid${node.id}`,
      originalName: node.name,
      relpath,
      pathSegments,
      validationIssues: issues,
      excluded,
      exclusionReason,
      nodeRef: node,
    }),
  );

  let preparedFiles: PreparedFile[] | null = null;
  // Did we actually open an editor session? When manual validation is skipped we
  // never own the modal, so we must not touch it afterwards: closing it (or
  // clearing its "submit in progress" flag) would hit whichever session owns it
  // and release its queue slot early.
  let sessionOpened = false;

  if (skipManualValidation) {
    const draftFiles = prepareMetadataDraftFiles(
      preparableFiles,
      settings,
      JobMode.CONVERT,
      nodesFallbacks,
    );
    const autoPreparation = validateMetadataDraftFiles({
      files: draftFiles.filter((file) => !file.excluded),
      excludedFilesCount: draftFiles.filter((file) => file.excluded).length,
      dictionaries: settings.dictionaries,
    });

    if (autoPreparation.validation.blockingIssueCount === 0) {
      preparedFiles = buildPreparedFiles(
        draftFiles.filter((file) => !file.excluded),
        autoPreparation.newFilenames,
      );
    }
  }

  if (!preparedFiles) {
    // 2. Open MetadataEditor and wait for user — pass node fallbacks for pre-filling
    sessionOpened = true;
    preparedFiles = await requestUserValidation(preparableFiles, settings, {
      mode: JobMode.CONVERT,
      fallback: nodesFallbacks,
    });
  }

  if (!preparedFiles) return null; // user cancelled

  try {
    if (signal.aborted) return null;

    // 3. Execute renames (files that need renaming)
    const renameResults = await Promise.all(
      preparedFiles
        .filter((f) => f.originalName !== f.newFilename)
        .map((f) => renameForConvert(f, signal)),
    );

    if (signal.aborted) return null;

    const failed = renameResults.filter((r) => !r.success);
    if (failed.length > 0) {
      throw new Error(
        `Failed to rename files: ${failed.map((f) => !f.success && f.error).join(', ')}`,
      );
    }

    // 4. Compute metadata diff (for converter caching)
    // Build id → fallback map so we can look up by file id, not by index.
    // (preparedFiles may be shorter than preparableFiles when some files are excluded)
    const fallbackById = new Map<string, FallbackDictionary>(
      preparableFiles.map((f, i) => [f.id, nodesFallbacks[i]?.fallback ?? {}]),
    );
    const metaDiff: Record<string, string> = {};
    for (const preparedFile of preparedFiles) {
      const originalMeta = fallbackById.get(preparedFile.id) ?? {};
      const finalMeta = preparedFile.meta ?? {};
      for (const k in finalMeta) {
        if (finalMeta[k] && finalMeta[k] !== originalMeta[k]) {
          metaDiff[k] = finalMeta[k];
        }
      }
    }

    return { preparedFiles, metaDiff };
  } finally {
    // Only the session we opened ourselves is ours to close. With
    // `skipManualValidation` the editor was never opened, so there is nothing to
    // reset and nothing to close here.
    if (sessionOpened) {
      _setSubmitInProgress(false);
      _closeEditor();
    }
  }
}
